import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Plus, Trash2, Pencil, Trophy, Link as LinkIcon, Copy, Calendar, Clock,
  ChevronLeft, Users, DollarSign, CheckCircle2, XCircle, ArrowLeft, Image as ImageIcon,
  MessageCircle, MessageSquare, Power, PowerOff, Users2, Send,
} from "lucide-react";

// Template padrao da mensagem WhatsApp INDIVIDUAL (pode ser editado por bolao)
const DEFAULT_MSG_TEMPLATE = `Olá {nome}! 👋

Obrigado por participar do nosso bolão *{bolao}*! 🏆

Segue o link abaixo para acompanhar seus palpites e o resultado:
{link}

📱 Entre no grupo do WhatsApp para ficar por dentro de tudo:
{grupo}`;

// Template padrao da mensagem para GRUPO (anuncio do bolao)
const DEFAULT_GRUPO_TEMPLATE = `🏆 *NOVO BOLÃO DISPONÍVEL!*

📋 *{bolao}*

⚽ Jogos:
{jogos}

💰 Valor do palpite: {valor}
🗓 Encerramento dos palpites:
{encerramento}

🎯 Para participar, acesse o link abaixo:
{link}

💬 Dúvidas? Fale com o responsável:
{whatsapp1}`;

const fmtCurrency = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const STATUS_LABELS = { aberto: "Aberto", encerrado: "Encerrado", finalizado: "Finalizado" };
const STATUS_COLORS = {
  aberto: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  encerrado: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  finalizado: "bg-blue-500/15 text-blue-400 border-blue-500/30",
};

// Formata "YYYY-MM-DDTHH:MM" (horario de Brasilia) em pt-BR
function fmtDataHoraBR(s) {
  if (!s) return "-";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return s;
  const [, Y, M, D, h, mn] = m;
  return `${D}/${M}/${Y} às ${h}:${mn} (horário de Brasília)`;
}

const emptyJogo = () => ({ time1: "", logo1: "", time2: "", logo2: "", data: "", hora: "" });

export default function BolaoPage() {
  const { authAxios } = useAuth();
  const [view, setView] = useState("list"); // list | detail
  const [stats, setStats] = useState({ total: 0, abertos: 0, finalizados: 0, arrecadado: 0 });
  const [boloes, setBoloes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  // Dialog: novo/editar bolao
  const [dlgBolao, setDlgBolao] = useState(false);
  const [editBolao, setEditBolao] = useState(null);
  const [savingBolao, setSavingBolao] = useState(false);
  const [bolaoForm, setBolaoForm] = useState({
    nome: "", valor_palpite: "", encerramento: "", status: "aberto", jogos: [emptyJogo()],
    whatsapp_responsavel1: "", whatsapp_responsavel2: "", link_grupo_whatsapp: "",
  });

  // Dialog: participante
  const [dlgPart, setDlgPart] = useState(false);
  const [editPart, setEditPart] = useState(null);
  const [savingPart, setSavingPart] = useState(false);
  const [partForm, setPartForm] = useState({
    nome: "", whatsapp: "", valor_pago: "", pago: false, palpites: [],
  });

  // Dialog: resultado
  const [dlgResult, setDlgResult] = useState(false);
  const [resultForm, setResultForm] = useState([]);

  // Dialog: editar mensagem WhatsApp
  const [dlgMsg, setDlgMsg] = useState(false);
  const [msgTemplate, setMsgTemplate] = useState("");
  const [savingMsg, setSavingMsg] = useState(false);

  // Dialog: editar mensagem do GRUPO
  const [dlgGrupo, setDlgGrupo] = useState(false);
  const [grupoTemplate, setGrupoTemplate] = useState("");
  const [grupoLinkEdit, setGrupoLinkEdit] = useState("");
  const [savingGrupo, setSavingGrupo] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const [stRes, bRes] = await Promise.all([
        authAxios.get("/bolao/stats"),
        authAxios.get("/bolao"),
      ]);
      setStats(stRes.data);
      setBoloes(bRes.data);
    } catch {
      toast.error("Erro ao carregar boloes");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  const fetchDetail = useCallback(async (id) => {
    try {
      const res = await authAxios.get(`/bolao/${id}`);
      setDetail(res.data);
    } catch {
      toast.error("Erro ao carregar bolao");
    }
  }, [authAxios]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    if (view === "detail" && selectedId) fetchDetail(selectedId);
  }, [view, selectedId, fetchDetail]);

  // ───── Bolão Create/Edit
  const openNovoBolao = () => {
    setEditBolao(null);
    setBolaoForm({
      nome: "", valor_palpite: "", encerramento: "", status: "aberto", jogos: [emptyJogo()],
      whatsapp_responsavel1: "", whatsapp_responsavel2: "", link_grupo_whatsapp: "",
    });
    setDlgBolao(true);
  };

  const openEditBolao = (b) => {
    setEditBolao(b);
    setBolaoForm({
      nome: b.nome || "",
      valor_palpite: b.valor_palpite || "",
      encerramento: b.encerramento || "",
      status: b.status || "aberto",
      whatsapp_responsavel1: b.whatsapp_responsavel1 || "",
      whatsapp_responsavel2: b.whatsapp_responsavel2 || "",
      link_grupo_whatsapp: b.link_grupo_whatsapp || "",
      jogos: (b.jogos && b.jogos.length > 0) ? b.jogos.map(j => ({
        time1: j.time1 || "", logo1: j.logo1 || "",
        time2: j.time2 || "", logo2: j.logo2 || "",
        data: j.data || "", hora: j.hora || "",
      })) : [emptyJogo()],
    });
    setDlgBolao(true);
  };

  const addJogoForm = () => setBolaoForm(f => ({ ...f, jogos: [...f.jogos, emptyJogo()] }));
  const removeJogoForm = (idx) => setBolaoForm(f => ({ ...f, jogos: f.jogos.filter((_, i) => i !== idx) }));
  const updateJogoField = (idx, field, val) => setBolaoForm(f => ({
    ...f, jogos: f.jogos.map((j, i) => i === idx ? { ...j, [field]: val } : j)
  }));

  const submitBolao = async () => {
    if (!bolaoForm.nome.trim()) { toast.error("Informe o nome do bolão"); return; }
    if (!bolaoForm.encerramento) { toast.error("Informe a data/hora de encerramento"); return; }
    const jogosValidos = bolaoForm.jogos.filter(j => j.time1.trim() && j.time2.trim());
    if (jogosValidos.length === 0) { toast.error("Adicione ao menos 1 jogo válido"); return; }

    setSavingBolao(true);
    try {
      const payload = {
        nome: bolaoForm.nome.trim(),
        valor_palpite: Number(bolaoForm.valor_palpite) || 0,
        encerramento: bolaoForm.encerramento,
        status: bolaoForm.status,
        jogos: jogosValidos,
        whatsapp_responsavel1: bolaoForm.whatsapp_responsavel1 || "",
        whatsapp_responsavel2: bolaoForm.whatsapp_responsavel2 || "",
        link_grupo_whatsapp: bolaoForm.link_grupo_whatsapp || "",
      };
      if (editBolao) {
        await authAxios.put(`/bolao/${editBolao.id}`, payload);
        toast.success("Bolão atualizado!");
        if (view === "detail") fetchDetail(editBolao.id);
      } else {
        await authAxios.post("/bolao", payload);
        toast.success("Bolão criado!");
      }
      setDlgBolao(false);
      fetchList();
    } catch {
      toast.error("Erro ao salvar bolão");
    } finally {
      setSavingBolao(false);
    }
  };

  const handleDeleteBolao = async (id) => {
    if (!window.confirm("Excluir este bolão? Esta ação não pode ser desfeita.")) return;
    try {
      await authAxios.delete(`/bolao/${id}`);
      toast.success("Bolão removido!");
      if (view === "detail") setView("list");
      fetchList();
    } catch { toast.error("Erro ao remover"); }
  };

  // ───── Participantes
  const openNovoParticipante = () => {
    setEditPart(null);
    setPartForm({
      nome: "", whatsapp: "", valor_pago: detail?.valor_palpite || "", pago: false,
      palpites: (detail?.jogos || []).map(j => ({ jogo_id: j.id, gols1: 0, gols2: 0 })),
    });
    setDlgPart(true);
  };

  const openEditParticipante = (p) => {
    setEditPart(p);
    // garantir um palpite para cada jogo
    const palpitesMap = {};
    (p.palpites || []).forEach(pl => { palpitesMap[pl.jogo_id] = pl; });
    const palpites = (detail?.jogos || []).map(j => palpitesMap[j.id] || { jogo_id: j.id, gols1: 0, gols2: 0 });
    setPartForm({
      nome: p.nome || "", whatsapp: p.whatsapp || "",
      valor_pago: p.valor_pago || "", pago: !!p.pago,
      palpites,
    });
    setDlgPart(true);
  };

  const submitParticipante = async () => {
    if (!partForm.nome.trim()) { toast.error("Informe o nome"); return; }
    setSavingPart(true);
    try {
      const payload = {
        nome: partForm.nome.trim(),
        whatsapp: partForm.whatsapp || "",
        valor_pago: Number(partForm.valor_pago) || 0,
        pago: partForm.pago,
        palpites: partForm.palpites.map(p => ({
          jogo_id: p.jogo_id,
          gols1: Number(p.gols1) || 0,
          gols2: Number(p.gols2) || 0,
        })),
      };
      if (editPart) {
        await authAxios.put(`/bolao/${detail.id}/participantes/${editPart.id}`, payload);
        toast.success("Participante atualizado!");
      } else {
        await authAxios.post(`/bolao/${detail.id}/participantes`, payload);
        toast.success("Participante adicionado!");
      }
      setDlgPart(false);
      fetchDetail(detail.id);
      fetchList();
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSavingPart(false);
    }
  };

  const handleDeletePart = async (pid) => {
    if (!window.confirm("Excluir este participante?")) return;
    try {
      await authAxios.delete(`/bolao/${detail.id}/participantes/${pid}`);
      toast.success("Removido!");
      fetchDetail(detail.id);
      fetchList();
    } catch { toast.error("Erro ao remover"); }
  };

  // ───── Resultado
  const openResultado = () => {
    setResultForm((detail?.jogos || []).map(j => ({
      jogo_id: j.id, gols1: j.gols1 ?? 0, gols2: j.gols2 ?? 0, label: `${j.time1} x ${j.time2}`,
    })));
    setDlgResult(true);
  };

  const submitResultado = async () => {
    try {
      await authAxios.post(`/bolao/${detail.id}/resultado`, {
        resultados: resultForm.map(r => ({ jogo_id: r.jogo_id, gols1: Number(r.gols1) || 0, gols2: Number(r.gols2) || 0 })),
      });
      toast.success("Resultado salvo! Ganhadores calculados.");
      setDlgResult(false);
      fetchDetail(detail.id);
      fetchList();
    } catch { toast.error("Erro ao salvar resultado"); }
  };

  const copyLink = (codigo) => {
    const url = `${window.location.origin}/bolao/${codigo}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const enviarWhatsApp = (participante) => {
    const numero = (participante.whatsapp || "").replace(/\D/g, "");
    if (!numero) { toast.error("Este participante não tem WhatsApp cadastrado."); return; }
    const linkBolao = `${window.location.origin}/bolao/${detail.codigo_publico}`;
    const linkGrupo = detail.link_grupo_whatsapp || "";
    const template = detail.mensagem_whatsapp || DEFAULT_MSG_TEMPLATE;
    let msg = template
      .replace(/\{nome\}/g, participante.nome || "")
      .replace(/\{bolao\}/g, detail.nome || "")
      .replace(/\{link\}/g, linkBolao)
      .replace(/\{grupo\}/g, linkGrupo);
    // Remove linhas que ficaram so com "grupo" vazio
    if (!linkGrupo) {
      msg = msg.replace(/\n*📱[^\n]*\n+\s*$/g, "").trim();
    }
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  const openEditMensagem = () => {
    setMsgTemplate(detail.mensagem_whatsapp || DEFAULT_MSG_TEMPLATE);
    setDlgMsg(true);
  };

  const salvarMensagem = async () => {
    setSavingMsg(true);
    try {
      await authAxios.put(`/bolao/${detail.id}`, { mensagem_whatsapp: msgTemplate });
      toast.success("Mensagem atualizada!");
      setDlgMsg(false);
      fetchDetail(detail.id);
    } catch { toast.error("Erro ao salvar mensagem"); }
    finally { setSavingMsg(false); }
  };

  const restaurarMsgPadrao = () => {
    setMsgTemplate(DEFAULT_MSG_TEMPLATE);
    toast.info("Mensagem padrão restaurada. Clique em Salvar para aplicar.");
  };

  const togglePalpitePublico = async () => {
    const atual = detail.palpite_publico_ativo !== false;
    try {
      await authAxios.put(`/bolao/${detail.id}`, { palpite_publico_ativo: !atual });
      toast.success(atual ? "Palpites públicos DESATIVADOS" : "Palpites públicos ATIVADOS");
      fetchDetail(detail.id);
    } catch { toast.error("Erro ao alterar"); }
  };

  // Helpers da mensagem do GRUPO
  const buildJogosTexto = () => {
    return (detail?.jogos || [])
      .map((j) => `• ${j.time1} x ${j.time2} (${j.data?.split("-").reverse().join("/")} às ${j.hora})`)
      .join("\n");
  };

  const renderGrupoMessage = (template) => {
    const linkBolao = `${window.location.origin}/bolao/${detail.codigo_publico}`;
    const enc = detail.encerramento
      ? `${detail.encerramento.split("T")[0].split("-").reverse().join("/")} às ${detail.encerramento.split("T")[1] || ""} (horário de Brasília)`
      : "";
    const wpp1 = (detail.whatsapp_responsavel1 || "").replace(/\D/g, "");
    const wpp1Link = wpp1 ? `https://wa.me/${wpp1}` : "";
    return (template || DEFAULT_GRUPO_TEMPLATE)
      .replace(/\{bolao\}/g, detail.nome || "")
      .replace(/\{jogos\}/g, buildJogosTexto())
      .replace(/\{valor\}/g, fmtCurrency(detail.valor_palpite))
      .replace(/\{encerramento\}/g, enc)
      .replace(/\{link\}/g, linkBolao)
      .replace(/\{whatsapp1\}/g, wpp1Link || (detail.whatsapp_responsavel1 || "(não cadastrado)"));
  };

  const openEditGrupo = () => {
    setGrupoTemplate(detail.mensagem_grupo || DEFAULT_GRUPO_TEMPLATE);
    setGrupoLinkEdit(detail.link_grupo_whatsapp || "");
    setDlgGrupo(true);
  };

  const salvarGrupo = async () => {
    setSavingGrupo(true);
    try {
      await authAxios.put(`/bolao/${detail.id}`, {
        mensagem_grupo: grupoTemplate,
        link_grupo_whatsapp: grupoLinkEdit,
      });
      toast.success("Mensagem e link do grupo salvos!");
      setDlgGrupo(false);
      fetchDetail(detail.id);
    } catch { toast.error("Erro ao salvar"); }
    finally { setSavingGrupo(false); }
  };

  const restaurarGrupoPadrao = () => {
    setGrupoTemplate(DEFAULT_GRUPO_TEMPLATE);
    toast.info("Mensagem padrão restaurada. Clique em Salvar para aplicar.");
  };

  const enviarParaGrupo = () => {
    const msg = renderGrupoMessage(detail.mensagem_grupo);
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
    toast.info("Selecione o grupo no WhatsApp para enviar.");
  };

  // ─────────────────── RENDER ───────────────────

  if (loading && view === "list") {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  // ───── LIST VIEW ─────
  if (view === "list") {
    return (
      <div className="space-y-6" data-testid="bolao-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/15 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Bolão</h2>
              <p className="text-sm text-muted-foreground">Gerencie seus bolões de futebol</p>
            </div>
          </div>
          <Button onClick={openNovoBolao} data-testid="novo-bolao-btn">
            <Plus className="w-4 h-4 mr-1" /> Novo Bolão
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><Trophy className="w-4 h-4 text-blue-400" /><span className="text-xs text-muted-foreground">Total de Bolões</span></div>
              <p className="text-2xl font-bold text-blue-400" data-testid="stat-total">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-500/20 bg-emerald-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-xs text-muted-foreground">Abertos</span></div>
              <p className="text-2xl font-bold text-emerald-400" data-testid="stat-abertos">{stats.abertos}</p>
            </CardContent>
          </Card>
          <Card className="border-amber-500/20 bg-amber-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><XCircle className="w-4 h-4 text-amber-400" /><span className="text-xs text-muted-foreground">Finalizados</span></div>
              <p className="text-2xl font-bold text-amber-400" data-testid="stat-finalizados">{stats.finalizados}</p>
            </CardContent>
          </Card>
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-primary" /><span className="text-xs text-muted-foreground">Arrecadado</span></div>
              <p className="text-xl font-bold text-primary" data-testid="stat-arrecadado">{fmtCurrency(stats.arrecadado)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabela */}
        <Card>
          <CardContent className="p-4">
            {boloes.length === 0 ? (
              <p className="text-center text-muted-foreground py-12 text-sm">
                Nenhum bolão criado ainda. Clique em &quot;Novo Bolão&quot; para começar.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead className="text-center">Jogos</TableHead>
                      <TableHead className="text-center">Participantes</TableHead>
                      <TableHead className="text-right">Arrecadado</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {boloes.map((b) => (
                      <TableRow key={b.id} data-testid={`row-bolao-${b.id}`}>
                        <TableCell className="font-medium">{b.nome}</TableCell>
                        <TableCell className="text-center">{b.qtd_jogos}</TableCell>
                        <TableCell className="text-center">{b.qtd_participantes}</TableCell>
                        <TableCell className="text-right font-semibold text-emerald-400">{fmtCurrency(b.valor_arrecadado)}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={`${STATUS_COLORS[b.status] || ""}`}>{STATUS_LABELS[b.status] || b.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="outline" size="sm" onClick={() => copyLink(b.codigo_publico)} title="Copiar link público" data-testid={`copy-link-${b.id}`}>
                              <LinkIcon className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" onClick={() => { setSelectedId(b.id); setView("detail"); }} data-testid={`gerenciar-${b.id}`}>
                              Gerenciar
                            </Button>
                            <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => handleDeleteBolao(b.id)} data-testid={`delete-${b.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <BolaoFormDialog
          open={dlgBolao} onOpenChange={setDlgBolao}
          form={bolaoForm} setForm={setBolaoForm}
          addJogoForm={addJogoForm} removeJogoForm={removeJogoForm} updateJogoField={updateJogoField}
          onSubmit={submitBolao} saving={savingBolao} isEdit={!!editBolao}
        />
      </div>
    );
  }

  // ───── DETAIL VIEW ─────
  if (!detail) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const linkPublico = `${window.location.origin}/bolao/${detail.codigo_publico}`;
  const totalArrecadado = (detail.participantes || []).reduce((s, p) => s + (p.valor_pago || 0), 0);

  // Calcula ganhadores quando resultado informado
  const ganhadores = (() => {
    if (!detail.resultado_informado) return [];
    const jogosMap = {};
    (detail.jogos || []).forEach(j => { jogosMap[j.id] = [j.gols1, j.gols2]; });
    const totalJogos = (detail.jogos || []).length;
    const out = [];
    (detail.participantes || []).forEach(p => {
      let acertos = 0;
      (p.palpites || []).forEach(pl => {
        const r = jogosMap[pl.jogo_id];
        if (r && r[0] != null && r[1] != null && pl.gols1 === r[0] && pl.gols2 === r[1]) acertos++;
      });
      if (totalJogos > 0 && acertos === totalJogos) out.push({ ...p, acertos });
    });
    return out;
  })();
  const premioPorGanhador = ganhadores.length > 0 ? totalArrecadado / ganhadores.length : 0;

  return (
    <div className="space-y-6" data-testid="bolao-detail">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => { setView("list"); setDetail(null); }} data-testid="voltar-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold truncate flex items-center gap-2">
            <Trophy className="w-6 h-6 text-primary" /> {detail.nome}
          </h2>
          <p className="text-sm text-muted-foreground">
            Encerramento: {fmtDataHoraBR(detail.encerramento)} • <Badge className={STATUS_COLORS[detail.status]}>{STATUS_LABELS[detail.status]}</Badge>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => copyLink(detail.codigo_publico)} data-testid="copy-link-detail">
            <Copy className="w-4 h-4 mr-1" /> Copiar Link
          </Button>
          <Button variant="outline" size="sm" onClick={() => openEditBolao(detail)} data-testid="edit-bolao-btn">
            <Pencil className="w-4 h-4 mr-1" /> Editar
          </Button>
          <Button variant="outline" size="sm" onClick={openEditMensagem} className="text-emerald-400 hover:text-emerald-300 border-emerald-500/30" data-testid="edit-mensagem-btn">
            <MessageSquare className="w-4 h-4 mr-1" /> Msg Individual
          </Button>
          <Button variant="outline" size="sm" onClick={openEditGrupo} className="text-blue-400 hover:text-blue-300 border-blue-500/30" data-testid="edit-grupo-btn">
            <Users2 className="w-4 h-4 mr-1" /> Msg Grupo
          </Button>
          <Button size="sm" onClick={enviarParaGrupo} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="enviar-grupo-btn">
            <Send className="w-4 h-4 mr-1" /> Enviar p/ Grupo
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={togglePalpitePublico}
            className={detail.palpite_publico_ativo !== false
              ? "text-emerald-400 hover:text-emerald-300 border-emerald-500/30"
              : "text-red-400 hover:text-red-300 border-red-500/30"}
            data-testid="toggle-palpite-publico-btn"
            title={detail.palpite_publico_ativo !== false ? "Desativar palpites públicos" : "Ativar palpites públicos"}
          >
            {detail.palpite_publico_ativo !== false
              ? <><Power className="w-4 h-4 mr-1" /> Palpites: ON</>
              : <><PowerOff className="w-4 h-4 mr-1" /> Palpites: OFF</>}
          </Button>
          <Button size="sm" onClick={openResultado} className="bg-amber-500 hover:bg-amber-600" data-testid="informar-resultado-btn">
            <Trophy className="w-4 h-4 mr-1" /> Informar Resultado
          </Button>
        </div>
      </div>

      {/* Link público */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <LinkIcon className="w-5 h-5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">Link público exclusivo</p>
            <p className="text-sm font-mono truncate" data-testid="link-publico">{linkPublico}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => copyLink(detail.codigo_publico)}>
            <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.open(linkPublico, "_blank")}>
            Abrir
          </Button>
        </CardContent>
      </Card>

      {/* Stats do Bolão */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Valor do Palpite</p><p className="text-xl font-bold text-primary">{fmtCurrency(detail.valor_palpite)}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Jogos</p><p className="text-xl font-bold">{detail.jogos?.length || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Participantes</p><p className="text-xl font-bold">{detail.participantes?.length || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Arrecadado</p><p className="text-xl font-bold text-emerald-400">{fmtCurrency(totalArrecadado)}</p></CardContent></Card>
      </div>

      {/* Ganhadores (somente quando resultado informado) */}
      {detail.resultado_informado && (
        <Card className="border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-transparent" data-testid="ganhadores-card">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-amber-400">
              <Trophy className="w-5 h-5" /> Ganhadores
            </h3>
            {ganhadores.length === 0 ? (
              <p className="text-center text-muted-foreground py-4 text-sm">Não houve ganhadores neste bolão.</p>
            ) : (
              <>
                <div className="mb-3 p-3 rounded-lg bg-amber-500/15 border border-amber-500/30 text-center">
                  <p className="text-xs text-amber-300/80 mb-0.5">
                    {ganhadores.length > 1 ? `Prêmio dividido entre ${ganhadores.length} ganhadores` : "Prêmio total para o ganhador"}
                  </p>
                  <p className="text-2xl font-bold text-amber-400">
                    {fmtCurrency(premioPorGanhador)} <span className="text-sm text-amber-300/70 font-normal">por ganhador</span>
                  </p>
                </div>
                <div className="space-y-2">
                  {ganhadores.map((g) => (
                    <div key={g.id} className="flex items-center justify-between gap-2 border border-amber-500/20 rounded-lg p-3 bg-card/60">
                      <p className="font-medium flex items-center gap-2 min-w-0">
                        <Trophy className="w-4 h-4 text-amber-400 shrink-0" />
                        <span className="truncate">{g.nome}</span>
                      </p>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 font-bold">
                          {fmtCurrency(premioPorGanhador)}
                        </Badge>
                        <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30">
                          {g.acertos}/{detail.jogos.length}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Jogos */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Jogos</h3>
          <div className="grid gap-3 md:grid-cols-2">
            {(detail.jogos || []).map((j, idx) => (
              <div key={j.id} className="border border-border/60 rounded-lg p-3 bg-card/60">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">Jogo {idx + 1}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {j.data} {j.hora}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {j.logo1 ? <img src={j.logo1} alt={j.time1} className="w-8 h-8 object-contain" onError={(e) => e.target.style.display='none'} /> : <ImageIcon className="w-8 h-8 text-muted-foreground/40" />}
                    <span className="font-medium truncate">{j.time1}</span>
                  </div>
                  <div className="text-center px-2">
                    {j.gols1 != null && j.gols2 != null ? (
                      <span className="font-bold text-amber-400">{j.gols1} x {j.gols2}</span>
                    ) : (
                      <span className="text-muted-foreground text-sm">vs</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                    <span className="font-medium truncate">{j.time2}</span>
                    {j.logo2 ? <img src={j.logo2} alt={j.time2} className="w-8 h-8 object-contain" onError={(e) => e.target.style.display='none'} /> : <ImageIcon className="w-8 h-8 text-muted-foreground/40" />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Participantes */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> Participantes</h3>
            {!detail.resultado_informado && (
              <Button size="sm" onClick={openNovoParticipante} data-testid="add-participante-btn">
                <Plus className="w-4 h-4 mr-1" /> Adicionar Participante
              </Button>
            )}
          </div>
          {detail.resultado_informado && (
            <div className="mb-3 p-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> Resultado já informado. Não é possível adicionar/editar participantes.
            </div>
          )}
          {(detail.participantes || []).length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">Nenhum participante cadastrado ainda.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>WhatsApp</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-center">Pago</TableHead>
                    <TableHead>Palpites</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(detail.participantes || []).map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.nome}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.whatsapp || "-"}</TableCell>
                      <TableCell className="text-right">{fmtCurrency(p.valor_pago)}</TableCell>
                      <TableCell className="text-center">
                        {p.pago
                          ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Sim</Badge>
                          : <Badge className="bg-red-500/15 text-red-400 border-red-500/30">Não</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs space-y-0.5">
                          {(p.palpites || []).map((pl, i) => {
                            const j = detail.jogos.find(x => x.id === pl.jogo_id);
                            return j ? <div key={i}>{j.time1} {pl.gols1} x {pl.gols2} {j.time2}</div> : null;
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-30"
                            onClick={() => enviarWhatsApp(p)}
                            disabled={!p.whatsapp}
                            title={p.whatsapp ? "Enviar mensagem pelo WhatsApp" : "Participante sem WhatsApp cadastrado"}
                            data-testid={`whatsapp-${p.id}`}
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditParticipante(p)} disabled={detail.resultado_informado}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeletePart(p.id)} disabled={detail.resultado_informado}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}

      {/* Dialog Editar Mensagem WhatsApp */}
      <Dialog open={dlgMsg} onOpenChange={setDlgMsg}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-mensagem">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-emerald-400" /> Editar Mensagem do WhatsApp</DialogTitle>
            <DialogDescription>
              Personalize a mensagem enviada aos participantes. Use os placeholders abaixo:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {[
                { tag: "{nome}", desc: "Nome do participante" },
                { tag: "{bolao}", desc: "Nome do bolão" },
                { tag: "{link}", desc: "Link público" },
                { tag: "{grupo}", desc: "Link do grupo WhatsApp" },
              ].map((p) => (
                <button
                  key={p.tag}
                  type="button"
                  onClick={() => setMsgTemplate(t => t + p.tag)}
                  className="text-xs px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition"
                  title={p.desc}
                >
                  {p.tag}
                </button>
              ))}
            </div>
            <Textarea
              value={msgTemplate}
              onChange={(e) => setMsgTemplate(e.target.value)}
              rows={10}
              className="font-mono text-sm"
              placeholder="Digite a mensagem..."
              data-testid="msg-textarea"
            />
            <div className="p-3 rounded-md bg-card/60 border border-border/60">
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Pré-visualização:</p>
              <p className="text-sm whitespace-pre-wrap text-foreground/90">
                {msgTemplate
                  .replace(/\{nome\}/g, "João Silva")
                  .replace(/\{bolao\}/g, detail.nome)
                  .replace(/\{link\}/g, `${window.location.origin}/bolao/${detail.codigo_publico}`)
                  .replace(/\{grupo\}/g, detail.link_grupo_whatsapp || "https://chat.whatsapp.com/...")}
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" size="sm" onClick={restaurarMsgPadrao} className="sm:mr-auto" data-testid="restore-msg-btn">
              Restaurar Padrão
            </Button>
            <Button variant="outline" onClick={() => setDlgMsg(false)}>Cancelar</Button>
            <Button onClick={salvarMensagem} disabled={savingMsg} className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-msg-btn">
              {savingMsg ? "Salvando..." : "Salvar Mensagem"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Mensagem do GRUPO */}
      <Dialog open={dlgGrupo} onOpenChange={setDlgGrupo}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-grupo">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users2 className="w-5 h-5 text-blue-400" /> Mensagem do Grupo</DialogTitle>
            <DialogDescription>
              Configure o link do grupo e a mensagem que será enviada quando você clicar em &quot;Enviar p/ Grupo&quot;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Link do Grupo do WhatsApp</Label>
              <Input
                value={grupoLinkEdit}
                onChange={(e) => setGrupoLinkEdit(e.target.value)}
                placeholder="https://chat.whatsapp.com/XXXXXX"
                data-testid="grupo-link-input"
              />
              <p className="text-xs text-muted-foreground">Editável diretamente aqui — sem precisar abrir &quot;Editar Bolão&quot;.</p>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-border/40">
              {[
                { tag: "{bolao}", desc: "Nome do bolão" },
                { tag: "{jogos}", desc: "Lista de jogos" },
                { tag: "{valor}", desc: "Valor do palpite" },
                { tag: "{encerramento}", desc: "Data/hora de encerramento" },
                { tag: "{link}", desc: "Link público" },
                { tag: "{whatsapp1}", desc: "Link wa.me do responsável" },
              ].map((p) => (
                <button
                  key={p.tag}
                  type="button"
                  onClick={() => setGrupoTemplate(t => t + p.tag)}
                  className="text-xs px-2 py-1 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20 transition"
                  title={p.desc}
                >
                  {p.tag}
                </button>
              ))}
            </div>

            <Textarea
              value={grupoTemplate}
              onChange={(e) => setGrupoTemplate(e.target.value)}
              rows={12}
              className="font-mono text-sm"
              placeholder="Digite a mensagem..."
              data-testid="grupo-textarea"
            />

            <div className="p-3 rounded-md bg-card/60 border border-border/60">
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">Pré-visualização:</p>
              <p className="text-sm whitespace-pre-wrap text-foreground/90">
                {renderGrupoMessage(grupoTemplate)}
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" size="sm" onClick={restaurarGrupoPadrao} className="sm:mr-auto" data-testid="restore-grupo-btn">
              Restaurar Padrão
            </Button>
            <Button variant="outline" onClick={() => setDlgGrupo(false)}>Cancelar</Button>
            <Button onClick={salvarGrupo} disabled={savingGrupo} className="bg-blue-500 hover:bg-blue-600" data-testid="save-grupo-btn">
              {savingGrupo ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BolaoFormDialog
        open={dlgBolao} onOpenChange={setDlgBolao}
        form={bolaoForm} setForm={setBolaoForm}
        addJogoForm={addJogoForm} removeJogoForm={removeJogoForm} updateJogoField={updateJogoField}
        onSubmit={submitBolao} saving={savingBolao} isEdit={!!editBolao}
      />

      <Dialog open={dlgPart} onOpenChange={setDlgPart}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-participante">
          <DialogHeader>
            <DialogTitle>{editPart ? "Editar" : "Adicionar"} Participante</DialogTitle>
            <DialogDescription>Preencha os dados e os palpites para cada jogo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nome</Label>
                <Input value={partForm.nome} onChange={(e) => setPartForm(f => ({ ...f, nome: e.target.value }))} data-testid="part-nome" />
              </div>
              <div className="space-y-1.5">
                <Label>WhatsApp</Label>
                <Input value={partForm.whatsapp} onChange={(e) => setPartForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="(00) 00000-0000" />
              </div>
              <div className="space-y-1.5">
                <Label>Valor Pago (R$)</Label>
                <Input type="number" step="0.01" value={partForm.valor_pago} onChange={(e) => setPartForm(f => ({ ...f, valor_pago: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Pago</Label>
                <Select value={partForm.pago ? "true" : "false"} onValueChange={(v) => setPartForm(f => ({ ...f, pago: v === "true" }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">Não</SelectItem>
                    <SelectItem value="true">Sim</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-border/60 pt-3 space-y-2">
              <p className="text-sm font-semibold">Palpites</p>
              {partForm.palpites.map((pl, idx) => {
                const j = detail.jogos.find(x => x.id === pl.jogo_id);
                return (
                  <div key={pl.jogo_id} className="grid grid-cols-[1fr_60px_20px_60px_1fr] items-center gap-2 text-sm">
                    <span className="truncate">{j?.time1 || `Time ${idx*2+1}`}</span>
                    <Input type="number" min="0" value={pl.gols1} onChange={(e) => setPartForm(f => ({
                      ...f, palpites: f.palpites.map((p, i) => i === idx ? { ...p, gols1: e.target.value } : p)
                    }))} className="h-8 text-center" data-testid={`palpite-${idx}-g1`} />
                    <span className="text-center text-muted-foreground">x</span>
                    <Input type="number" min="0" value={pl.gols2} onChange={(e) => setPartForm(f => ({
                      ...f, palpites: f.palpites.map((p, i) => i === idx ? { ...p, gols2: e.target.value } : p)
                    }))} className="h-8 text-center" data-testid={`palpite-${idx}-g2`} />
                    <span className="truncate text-right">{j?.time2 || `Time ${idx*2+2}`}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgPart(false)}>Cancelar</Button>
            <Button onClick={submitParticipante} disabled={savingPart} data-testid="save-part-btn">
              {savingPart ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Resultado */}
      <Dialog open={dlgResult} onOpenChange={setDlgResult}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-resultado">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" /> Informar Resultado</DialogTitle>
            <DialogDescription>Preencha o placar oficial de cada jogo. Ganhadores serão calculados automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {resultForm.map((r, idx) => (
              <div key={r.jogo_id} className="grid grid-cols-[1fr_60px_20px_60px_1fr] items-center gap-2 text-sm">
                <span className="truncate">{r.label.split(" x ")[0]}</span>
                <Input type="number" min="0" value={r.gols1} onChange={(e) => setResultForm(arr => arr.map((x, i) => i === idx ? { ...x, gols1: e.target.value } : x))} className="h-8 text-center" />
                <span className="text-center">x</span>
                <Input type="number" min="0" value={r.gols2} onChange={(e) => setResultForm(arr => arr.map((x, i) => i === idx ? { ...x, gols2: e.target.value } : x))} className="h-8 text-center" />
                <span className="truncate text-right">{r.label.split(" x ")[1]}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgResult(false)}>Cancelar</Button>
            <Button onClick={submitResultado} className="bg-amber-500 hover:bg-amber-600" data-testid="save-resultado-btn">
              <Trophy className="w-4 h-4 mr-1" /> Salvar Resultado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ───────────────── Bolão Form Dialog (reutilizado) ─────────────────
function BolaoFormDialog({ open, onOpenChange, form, setForm, addJogoForm, removeJogoForm, updateJogoField, onSubmit, saving, isEdit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-bolao">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar Bolão" : "Novo Bolão"}</DialogTitle>
          <DialogDescription>Configure os dados básicos e adicione quantos jogos quiser.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Nome do Bolão</Label>
              <Input value={form.nome} onChange={(e) => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Bolão Brasileirão 2026" data-testid="bolao-nome" />
            </div>
            <div className="space-y-1.5">
              <Label>Valor do Palpite (R$)</Label>
              <Input type="number" step="0.01" value={form.valor_palpite} onChange={(e) => setForm(f => ({ ...f, valor_palpite: e.target.value }))} data-testid="bolao-valor" />
            </div>
            <div className="space-y-1.5">
              <Label>Encerramento dos Palpites</Label>
              <Input type="datetime-local" value={form.encerramento} onChange={(e) => setForm(f => ({ ...f, encerramento: e.target.value }))} data-testid="bolao-encerramento" />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aberto">Aberto</SelectItem>
                  <SelectItem value="encerrado">Encerrado</SelectItem>
                  <SelectItem value="finalizado">Finalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bloco WhatsApp */}
          <div className="border border-emerald-500/20 bg-emerald-500/5 rounded-lg p-3 space-y-3">
            <p className="text-sm font-semibold flex items-center gap-2 text-emerald-400">
              <MessageCircle className="w-4 h-4" /> Contatos WhatsApp (opcional)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">WhatsApp Responsável 1</Label>
                <Input
                  value={form.whatsapp_responsavel1}
                  onChange={(e) => setForm(f => ({ ...f, whatsapp_responsavel1: e.target.value }))}
                  placeholder="5511999999999"
                  data-testid="whatsapp-resp1"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">WhatsApp Responsável 2</Label>
                <Input
                  value={form.whatsapp_responsavel2}
                  onChange={(e) => setForm(f => ({ ...f, whatsapp_responsavel2: e.target.value }))}
                  placeholder="5511888888888"
                  data-testid="whatsapp-resp2"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Link do Grupo do WhatsApp</Label>
                <Input
                  value={form.link_grupo_whatsapp}
                  onChange={(e) => setForm(f => ({ ...f, link_grupo_whatsapp: e.target.value }))}
                  placeholder="https://chat.whatsapp.com/XXXXXX"
                  data-testid="link-grupo"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Apenas números (com DDI 55). O grupo será incluído na mensagem enviada aos participantes.
            </p>
          </div>

          <div className="border-t border-border/60 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-semibold">Jogos</p>
              <Button type="button" size="sm" variant="outline" onClick={addJogoForm} data-testid="add-jogo-btn">
                <Plus className="w-4 h-4 mr-1" /> Adicionar Jogo
              </Button>
            </div>

            {form.jogos.map((j, idx) => (
              <div key={idx} className="border border-border/60 rounded-lg p-3 space-y-3 bg-card/40">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-primary">Jogo {idx + 1}</p>
                  {form.jogos.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeJogoForm(idx)} data-testid={`remove-jogo-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Nome do Time {idx*2+1}</Label>
                    <Input value={j.time1} onChange={(e) => updateJogoField(idx, "time1", e.target.value)} placeholder={`Time ${idx*2+1}`} data-testid={`jogo-${idx}-time1`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">URL Imagem do Time {idx*2+1}</Label>
                    <Input value={j.logo1} onChange={(e) => updateJogoField(idx, "logo1", e.target.value)} placeholder="https://..." />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nome do Time {idx*2+2}</Label>
                    <Input value={j.time2} onChange={(e) => updateJogoField(idx, "time2", e.target.value)} placeholder={`Time ${idx*2+2}`} data-testid={`jogo-${idx}-time2`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">URL Imagem do Time {idx*2+2}</Label>
                    <Input value={j.logo2} onChange={(e) => updateJogoField(idx, "logo2", e.target.value)} placeholder="https://..." />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Data da Partida</Label>
                    <Input type="date" value={j.data} onChange={(e) => updateJogoField(idx, "data", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Horário</Label>
                    <Input type="time" value={j.hora} onChange={(e) => updateJogoField(idx, "hora", e.target.value)} />
                  </div>
                </div>
                {(j.logo1 || j.logo2) && (
                  <div className="flex items-center justify-center gap-4 pt-2">
                    {j.logo1 && <img src={j.logo1} alt="" className="w-10 h-10 object-contain" onError={(e) => e.target.style.display='none'} />}
                    <span className="text-xs text-muted-foreground">VS</span>
                    {j.logo2 && <img src={j.logo2} alt="" className="w-10 h-10 object-contain" onError={(e) => e.target.style.display='none'} />}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={onSubmit} disabled={saving} data-testid="save-bolao-btn">
            {saving ? "Salvando..." : (isEdit ? "Salvar Alterações" : "Criar Bolão")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
